import { compact } from 'lodash';
import { getRegionDisplayName } from 'shared/baby-grammar-rules';
import { tempDietaryTagMappings } from 'utils/constants';
import { CityPageCityFragment, CityPageFilterFragment, RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { encodeCityPageHref, encodeRegionPageHref, encodeZipPageHref } from 'utils/urls';

export interface Crumb {
  href: string;
  name: string;
}

export const HOME_CRUMB: Crumb = {
  href: '/',
  name: 'Food delivery',
};

export const rootBreadcrumbs = [HOME_CRUMB];
export const citiesRootBreadcrumbs = [
  HOME_CRUMB,
  {
    href: '/cities-we-serve',
    name: 'Shef Cities',
  },
];
export const zipsRootBreadcrumbs = [HOME_CRUMB, { href: '/zip-codes-we-serve', name: 'Shef ZIPs' }];
export const metroRootBreadcrumbs = [HOME_CRUMB, { href: '/metros-we-serve', name: 'Metros' }];

export const generateCityPageBreadcrumbs = (
  cityPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>,
  filter?: CityPageFilterFragment
): Crumb[] => {
  const cityUrl = encodeCityPageHref(cityPage);
  // Backtrack away from dietary filter
  const cuisineUrl = encodeCityPageHref(cityPage, filter && { ...filter, dietaryFilter: null });
  const dietaryUrl = encodeCityPageHref(cityPage, filter);

  const cityCrumb: Crumb | null = cityPage.cityName
    ? { href: cityUrl, name: `${cityPage.cityName}, ${cityPage.stateNameShort}` }
    : null;
  const cuisineCrumb: Crumb | null =
    filter?.cuisineCategoryId && filter?.cuisineCategory?.title
      ? { href: cuisineUrl, name: filter.cuisineCategory.title }
      : null;
  const dietaryCrumb: Crumb | null = filter?.dietaryFilter
    ? { href: dietaryUrl, name: tempDietaryTagMappings[filter.dietaryFilter] }
    : null;

  return compact([HOME_CRUMB, cityCrumb, cuisineCrumb, dietaryCrumb]);
};

export const generateZipPageBreadcrumbs = (
  zipCode: string,
  cityPage: Pick<CityPageCityFragment, 'cityName' | 'stateNameShort'>,
  filter?: CityPageFilterFragment
): Crumb[] => {
  const cityUrl = encodeCityPageHref(cityPage);
  const zipUrl = encodeZipPageHref(cityPage, zipCode);
  // Backtrack away from dietary filter
  const cuisineUrl = encodeZipPageHref(cityPage, zipCode, filter && { ...filter, dietaryFilter: null });
  const dietaryUrl = encodeZipPageHref(cityPage, zipCode, filter);

  const cityCrumb: Crumb | null = cityPage.cityName
    ? { href: cityUrl, name: `${cityPage.cityName}, ${cityPage.stateNameShort}` }
    : null;
  const zipCrumb: Crumb | null = zipCode ? { href: zipUrl, name: zipCode } : null;
  const cuisineCrumb: Crumb | null =
    filter?.cuisineCategoryId && filter?.cuisineCategory?.title
      ? { href: cuisineUrl, name: filter.cuisineCategory.title }
      : null;
  const dietaryCrumb: Crumb | null = filter?.dietaryFilter
    ? { href: dietaryUrl, name: tempDietaryTagMappings[filter.dietaryFilter] }
    : null;

  return compact([HOME_CRUMB, cityCrumb, zipCrumb, cuisineCrumb, dietaryCrumb]);
};

export const generateRegionPageBreadcrumbs = (region: Pick<RegionInfoFragment, 'name' | 'displayName'>): Crumb[] => {
  const regionUrl = encodeRegionPageHref(region);

  const displayName = getRegionDisplayName(region);
  const regionCrumb: Crumb | null = { href: regionUrl, name: displayName };

  return compact([HOME_CRUMB, regionCrumb]);
};
